<template>
  <DetailDialog
    title="外部リンク"
    icon="mdi-open-in-new"
    :width="860"
    body-padding="24px"
    cancel-min-width="128"
    :save-disabled="!isFormValid"
    :is-saving="isSaving"
    :show-delete="showDelete"
    @cancel="$emit('cancel')"
    @save="save"
    @delete="$emit('delete')"
  >
    <div class="external-link-grid">
      <div class="field-block title-block">
        <span class="field-label">タイトル</span>
        <label
          v-for="option in titleTypeOptions"
          :key="option.value"
          class="choice-row"
        >
          <input
            v-model="form.titleType"
            type="radio"
            :value="option.value"
          >
          <span>{{ option.label }}</span>
        </label>
        <input
          v-if="form.titleType === TITLE_TYPES.other"
          v-model="form.titleText"
          class="text-input"
          type="text"
        >
      </div>
      <div class="field-block url-block">
        <span class="field-label">URL</span>
        <input
          v-model="form.url"
          class="text-input"
          :class="{ 'text-input--error': urlError }"
          type="url"
          @blur="urlTouched = true"
        >
        <span
          v-if="urlError"
          class="field-error"
        >
          {{ urlError }}
        </span>
      </div>
    </div>
  </DetailDialog>
</template>

<script>
import DetailDialog from "@/vue/shared/ui/DetailDialog.vue";

// face_sheet_external_links.title_type（FaceSheetExternalLink::TITLE_TYPES）と一致させる。
const TITLE_TYPES = { review: 1, other: 2 };

const titleTypeOptions = [
  { value: TITLE_TYPES.review, label: "対応検討" },
  { value: TITLE_TYPES.other, label: "その他" }
];

export default {
  name: "FaceSheetExternalLinkDetailWindow",
  components: {
    DetailDialog
  },
  props: {
    externalLink: {
      type: Object,
      required: true
    },
    // 保存処理中フラグ。true の間は保存ボタンを無効化し連打を防ぐ。
    isSaving: {
      type: Boolean,
      default: false
    },
    // 削除ボタンを表示するかどうか（既存レコードを開いたときのみ true）。
    showDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "save", "delete"],
  data() {
    return {
      TITLE_TYPES,
      titleTypeOptions,
      form: this.createForm(this.externalLink),
      urlTouched: false
    };
  },
  computed: {
    // http(s):// から始まる正しいURLかどうかを判定する。
    isUrlValid() {
      const url = (this.form.url || "").trim();
      if (!url) return false;
      try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch {
        return false;
      }
    },
    isFormValid() {
      return this.isUrlValid;
    },
    // 未入力・不正な形式のときにエラー文言を返す。未タッチかつ空のときは表示しない。
    urlError() {
      const url = (this.form.url || "").trim();
      if (!url) {
        return this.urlTouched ? "URLを入力してください" : "";
      }
      if (!this.isUrlValid) {
        return "http:// または https:// から始まる正しいURLを入力してください";
      }
      return "";
    }
  },
  watch: {
    externalLink: {
      handler(externalLink) {
        this.form = this.createForm(externalLink);
        this.urlTouched = false;
      },
      immediate: true
    }
  },
  methods: {
    createForm(externalLink) {
      return {
        titleType: externalLink?.titleType ?? TITLE_TYPES.review,
        titleText: externalLink?.titleText || "",
        registeredAt: externalLink?.registeredAt || "",
        url: externalLink?.url || ""
      };
    },
    save() {
      this.urlTouched = true;
      if (!this.isFormValid) return;

      const payload = {
        id: this.externalLink?.id,
        lockVersion: this.externalLink?.lockVersion,
        title_type: this.form.titleType,
        title_text: this.form.titleType === TITLE_TYPES.other ? this.form.titleText : "",
        url: this.form.url
      };
      // 登録日時はユーザーが編集する項目ではなく、新規登録時に現在日時で自動設定する。
      if (!this.form.registeredAt) {
        payload.registered_at = new Date().toISOString();
      }

      this.$emit("save", payload);
    }
  }
};
</script>

<style lang="scss" scoped>
.external-link-grid {
  display: grid;
  gap: 28px;
  grid-template-columns: 220px minmax(0, 1fr);
}

.field-error {
  color: #e64779;
  font-size: 12px;
  line-height: 1.4;
}

.text-input--error {
  border-color: #e64779 !important;
}

@media (max-width: 820px) {
  .external-link-grid {
    gap: 16px;
    grid-template-columns: 1fr;
  }
}
</style>